import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Users, UserPlus, HeartHandshake, TrendingUp, CalendarCheck, Activity, Cake, PartyPopper, Send, AlertTriangle, LifeBuoy, ArrowRight,
  Rocket, CheckCircle2, Circle, GraduationCap, ShieldCheck, RefreshCw, LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import { ddmm } from "../lib/dates";
import { SignupReport } from "../components/SignupReport";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

function Stat({ icon: Icon, label, value, tint, testid, suffix }) {
  return (
    <div data-testid={testid} className={`${CARD} p-6 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(45,45,45,0.08)]`}>
      <div className="flex items-center justify-between">
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${tint}1A` }}>
          <Icon className="h-6 w-6" style={{ color: tint }} />
        </div>
      </div>
      <div className="mt-5 font-head text-4xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>
        {value}{suffix}
      </div>
      <div className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>{label}</div>
    </div>
  );
}

function CareTile({ icon: Icon, label, value, tint, testid, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`${CARD} p-5 flex items-center gap-4 text-left w-full transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(45,45,45,0.08)] group`}>
      <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${tint}1A` }}>
        <Icon className="h-6 w-6" style={{ color: tint }} />
      </div>
      <div className="flex-1">
        <div className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>{value}</div>
        <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>{label}</div>
      </div>
      <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: tint }} />
    </button>
  );
}

function GettingStarted({ setup, navigate }) {
  const steps = [
    { key: "members", label: "Add your first members", hint: "Add people one by one or bulk-import a CSV", to: "/members", cta: "Go to Members", icon: UserPlus },
    { key: "staff", label: "Set up your team & access", hint: "Make someone a Pastor/Leader or assign Finance/Pastoral teams", to: "/users", cta: "Users & Access", icon: ShieldCheck },
    { key: "services", label: "Log your first service attendance", hint: "Create a service and mark who attended", to: "/attendance", cta: "Go to Attendance", icon: CalendarCheck },
    { key: "courses", label: "Create a discipleship course", hint: "Add a course and lessons members can enrol in", to: "/classes", cta: "Go to Courses", icon: GraduationCap },
  ];
  const done = steps.filter((s) => setup[s.key]).length;
  if (done === steps.length) return null;
  return (
    <div className={`${CARD} p-6 md:p-8`} data-testid="getting-started">
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "#0B5E4E1A" }}>
          <Rocket className="h-6 w-6" style={{ color: "var(--sj-primary)" }} />
        </div>
        <div>
          <h3 className="font-head text-xl font-semibold">Getting started</h3>
          <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>{done} of {steps.length} done — let's set up your church portal.</p>
        </div>
      </div>
      <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ background: "var(--sj-border)" }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${(done / steps.length) * 100}%`, background: "var(--sj-secondary)" }} />
      </div>
      <div className="mt-5 space-y-2">
        {steps.map((s) => {
          const complete = !!setup[s.key];
          return (
            <div key={s.key} data-testid={`setup-step-${s.key}`} className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: "var(--sj-border)", background: complete ? "#17A88A0A" : "#fff" }}>
              {complete ? <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "var(--sj-secondary)" }} /> : <Circle className="h-5 w-5 shrink-0" style={{ color: "var(--sj-text-3)" }} />}
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm" style={{ color: complete ? "var(--sj-text-3)" : "var(--sj-text)", textDecoration: complete ? "line-through" : "none" }}>{s.label}</div>
                {!complete && <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{s.hint}</div>}
              </div>
              {!complete && (
                <button data-testid={`setup-cta-${s.key}`} onClick={() => navigate(s.to)} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 text-white hover:brightness-105 shrink-0" style={{ background: "var(--sj-primary)" }}>
                  <s.icon className="h-3.5 w-3.5" /> {s.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [att, setAtt] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [setup, setSetup] = useState(null);
  const [careSummary, setCareSummary] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const [tab, setTab] = useState("overview");

  const recheckAll = async () => {
    setRechecking(true);
    try {
      const { data } = await api.post("/admin/recheck-all-at-risk");
      toast.success(data.message || "Rechecked");
      try { const cs = await api.get("/care/summary"); setCareSummary(cs.data); } catch (e) {}
    } catch (err) { toast.error(err?.response?.data?.detail || "Recheck failed"); }
    finally { setRechecking(false); }
  };

  useEffect(() => {
    (async () => {
      const [s, a, g, r] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/attendance-trend"),
        api.get("/dashboard/growth-trend"),
        api.get("/reminders/upcoming"),
      ]);
      setStats(s.data); setAtt(a.data); setGrowth(g.data); setReminders(r.data);
      try {
        const sv = await api.get("/services");
        const today = new Date().toISOString().slice(0, 10);
        const up = (sv.data || [])
          .filter((x) => (x.service_date || "") >= today)
          .sort((x, y) => (x.service_date + (x.start_time || "")).localeCompare(y.service_date + (y.start_time || "")))
          .slice(0, 4);
        setUpcoming(up);
      } catch (err) { /* silent */ }
      if (user?.role === "admin" || user?.caps?.pastoral_care) {
        try {
          const cs = await api.get("/care/summary");
          setCareSummary(cs.data);
        } catch (err) { console.error("Care summary load failed:", err); }
      }
      if (user?.role === "admin") {
        try {
          const [c, u] = await Promise.all([api.get("/courses"), api.get("/users")]);
          setSetup({
            members: s.data.total_members > 0,
            services: s.data.total_services > 0,
            courses: Array.isArray(c.data) && c.data.length > 0,
            staff: (u.data || []).some((x) => ["pastor", "leader", "team_leader"].includes(x.role)),
          });
        } catch (err) { console.error("Setup checklist load failed:", err); }
      }
    })();
  }, [user]);

  const sendWish = async (r) => {
    const msg = r.type === "birthday"
      ? `Happy Birthday ${r.name.split(" ")[0]}! The whole Streams of Joy Erith London family celebrates you today. 🎉`
      : `Happy ${r.years}-year membership anniversary ${r.name.split(" ")[0]}! Thank you for being part of the family. 🙏`;
    try {
      const { data } = await api.post("/reminders/send", { member_ids: [r.member_id], channel: "sms", message: msg });
      if (data.status === "simulated") toast.warning("Simulated — add Twilio keys to send real SMS");
      else toast.success(`Wishes sent to ${r.name}`);
    } catch { toast.error("Could not send"); }
  };

  const WHATS_NEW_KEY = "sj_whatsnew_2026_07";
  const [showWhatsNew, setShowWhatsNew] = useState(() => {
    try { return localStorage.getItem(WHATS_NEW_KEY) !== "dismissed"; } catch { return true; }
  });
  const dismissWhatsNew = () => {
    try { localStorage.setItem(WHATS_NEW_KEY, "dismissed"); } catch { /* noop */ }
    setShowWhatsNew(false);
  };

  const canPastoral = user?.role === "admin" || user?.caps?.pastoral_care;
  const TABS = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    ...(canPastoral ? [{ key: "pastoral", label: "Pastoral Care", icon: HeartHandshake }] : []),
    { key: "attendance", label: "Attendance & Conversion", icon: CalendarCheck },
  ];

  const overview = (
    <div className="space-y-8">
      {tab === "overview" && (
      <div className="sj-hero" data-testid="dashboard-hero">
        <div className="p-8 md:p-10">
          <div className="sj-eyebrow">Streams of Joy Erith London · Church Dashboard</div>
          <h1 className="mt-2 font-head text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            Welcome back, <span style={{ color: "var(--sj-accent)" }}>{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="mt-2 text-white/85 max-w-lg">
            Here's how the Streams of Joy Erith London family is growing this season.
          </p>
        </div>
      </div>
      )}

      {tab === "overview" && showWhatsNew && (
        <div data-testid="whats-new-banner" className="relative rounded-2xl border p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" style={{ borderColor: "#0B5E4E33", background: "linear-gradient(120deg,#17A88A12,#0B5E4E14)" }}>
          <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0 shadow-sm" style={{ background: "var(--sj-primary)" }}>
            <Rocket className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-head font-bold text-base" style={{ color: "var(--sj-text)" }}>What's new — pastoral care tools</div>
            <p className="text-sm mt-0.5" style={{ color: "var(--sj-text-2)" }}>
              New: live SMS &amp; email, per-member care history (with PDF), overdue filters, bulk check-ins, automated weekly check-ins and a message log. See the guide for a quick tour.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="whats-new-open" onClick={() => navigate("/guide")} className="rounded-full px-4 py-2.5 text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>
              Open User Guide
            </button>
            <button data-testid="whats-new-dismiss" onClick={dismissWhatsNew} className="rounded-full px-3 py-2.5 text-sm font-semibold border transition-colors hover:bg-white" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {tab === "overview" && setup && <GettingStarted setup={setup} navigate={navigate} />}

      {tab === "overview" && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Stat testid="stat-total-members" icon={Users} label="Total Members" value={stats.total_members} tint="#0B5E4E" />
          <Stat testid="stat-new-members" icon={UserPlus} label="New This Month" value={stats.new_this_month} tint="#17A88A" />
          <Stat testid="stat-conversions" icon={HeartHandshake} label="Conversions" value={stats.conversions} tint="#084438" />
          <Stat testid="stat-retention" icon={Activity} label="Retention Rate" value={stats.retention_rate} suffix="%" tint="#A8A29E" />
        </div>
      )}

      {tab === "overview" && upcoming.length > 0 && (
        <div className={`${CARD} p-6`} data-testid="upcoming-attendance-widget">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
              <h3 className="font-head text-lg font-semibold">Registered so far</h3>
            </div>
            <button data-testid="widget-open-attendance" onClick={() => navigate("/attendance")} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--sj-primary)" }}>Attendance <ArrowRight className="h-4 w-4" /></button>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Members who've tapped “Yes, I'm attending” for upcoming services.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {upcoming.map((s) => {
              const isNext = s === upcoming[0];
              return (
                <div key={s.id} data-testid={`widget-service-${s.id}`} className="rounded-xl p-4 border" style={{ borderColor: isNext ? "var(--sj-primary)" : "var(--sj-border)", background: isNext ? "#F2F6F5" : "#fff" }}>
                  <div className="text-xs font-semibold uppercase tracking-wide truncate" style={{ color: "var(--sj-text-3)" }}>{s.title}</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-head text-3xl font-extrabold" style={{ color: "var(--sj-primary)" }} data-testid={`widget-count-${s.id}`}>{s.checkin_count ?? 0}</span>
                    <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>registered</span>
                  </div>
                  <div className="mt-1 text-xs flex items-center gap-1" style={{ color: "var(--sj-text-2)" }}>
                    <Users className="h-3.5 w-3.5" />
                    {new Date(s.service_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}{s.start_time ? ` · ${s.start_time}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "pastoral" && careSummary && (user?.role === "admin" || user?.caps?.pastoral_care) && (
        <div data-testid="pastoral-summary">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <HeartHandshake className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
              <h2 className="font-head text-xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>Pastoral care summary</h2>
            </div>
            <button data-testid="bulk-recheck-btn" disabled={rechecking} onClick={recheckAll} title="Recompute at-risk flags across every course and clear anyone wrongly flagged" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border hover:bg-[#EEF0F2] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
              <RefreshCw className={`h-4 w-4 ${rechecking ? "animate-spin" : ""}`} /> Recheck at-risk
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <CareTile testid="care-summary-pastoral" icon={HeartHandshake} label="In pastoral care" value={careSummary.in_pastoral} tint="#0B5E4E" onClick={() => navigate("/pastoral-care")} />
            <CareTile testid="care-summary-overdue" icon={AlertTriangle} label="Overdue (7+ days)" value={careSummary.overdue} tint="#C0392B" onClick={() => navigate("/pastoral-care", { state: { overdueOnly: true } })} />
            <CareTile testid="care-summary-follow-up" icon={LifeBuoy} label="In follow-up" value={careSummary.in_follow_up} tint="#084438" onClick={() => navigate("/follow-up")} />
            <CareTile testid="care-summary-contacts" icon={Send} label="Contacts this week" value={careSummary.contacts_this_week} tint="#17A88A" onClick={() => navigate("/pastoral-care")} />
          </div>
        </div>
      )}

      {tab === "pastoral" && stats && (user?.caps?.pastoral_care) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="care-queue">
          <CareTile testid="care-new-week" icon={UserPlus} label="New This Week" value={stats.new_this_week} tint="#17A88A" onClick={() => navigate("/members", { state: { filter: "new" } })} />
          <CareTile testid="care-needs-review" icon={AlertTriangle} label="Needs Review" value={stats.needs_review} tint="#0B5E4E" onClick={() => navigate("/members", { state: { filter: "needs_review" } })} />
          <CareTile testid="care-in-follow-up" icon={LifeBuoy} label="In Follow-up" value={stats.in_follow_up} tint="#084438" onClick={() => navigate("/new-members")} />
        </div>
      )}

      {tab === "attendance" && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className={`${CARD} p-5 flex items-center gap-4`} data-testid="stat-attendance-rate">
            <CalendarCheck className="h-8 w-8" style={{ color: "#0B5E4E" }} />
            <div>
              <div className="font-head text-2xl font-bold">{stats.attendance_rate}%</div>
              <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Avg Attendance Rate</div>
            </div>
          </div>
          <div className={`${CARD} p-5 flex items-center gap-4`}>
            <Users className="h-8 w-8" style={{ color: "#17A88A" }} />
            <div>
              <div className="font-head text-2xl font-bold">{stats.active_members}</div>
              <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Active Members</div>
            </div>
          </div>
          <div className={`${CARD} p-5 flex items-center gap-4`}>
            <TrendingUp className="h-8 w-8" style={{ color: "#084438" }} />
            <div>
              <div className="font-head text-2xl font-bold">{stats.total_services}</div>
              <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Services Logged</div>
            </div>
          </div>
        </div>
      )}

      {tab === "overview" && reminders.length > 0 && (
        <div className={`${CARD} p-6`} data-testid="reminders-widget">
          <div className="flex items-center gap-2 mb-1">
            <PartyPopper className="h-5 w-5" style={{ color: "#0B5E4E" }} />
            <h3 className="font-head text-xl font-semibold">Upcoming Celebrations</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Birthdays & membership anniversaries in the next 30 days</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {reminders.slice(0, 6).map((r) => (
              <div key={`${r.type}-${r.member_id}`} data-testid={`reminder-${r.member_id}`} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor: "var(--sj-border)", background: "var(--sj-bg)" }}>
                <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ background: r.type === "birthday" ? "#0B5E4E1A" : "#17A88A1A" }}>
                  {r.type === "birthday" ? <Cake className="h-4.5 w-4.5" style={{ color: "#0B5E4E" }} /> : <PartyPopper className="h-4.5 w-4.5" style={{ color: "#17A88A" }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{r.name}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>
                    {r.type === "birthday" ? "Birthday" : `${r.years}yr anniversary`} · {r.days_until === 0 ? "Today!" : `in ${r.days_until}d`}
                  </div>
                </div>
                <button data-testid={`send-wish-${r.member_id}`} onClick={() => sendWish(r)} title="Send wishes" className="p-2 rounded-lg hover:bg-[#E3F1EC] shrink-0" style={{ color: "var(--sj-primary)" }}>
                  <Send className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "attendance" && (<>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${CARD} p-6 lg:col-span-2`}>
          <h3 className="font-head text-xl font-semibold mb-1">Attendance Trend</h3>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Self-registered check-ins vs recorded headcount across recent services</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={att} margin={{ left: -20 }}>
              <defs>
                <linearGradient id="gM" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#17A88A" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#17A88A" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0B5E4E" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0B5E4E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE9E4" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#56626A" }} tickFormatter={(d) => ddmm(d)} />
              <YAxis tick={{ fontSize: 11, fill: "#56626A" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EBE9E4" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" name="Self-registered" dataKey="checkins" stroke="#17A88A" strokeWidth={2.5} fill="url(#gM)" />
              <Area type="monotone" name="Headcount" dataKey="headcount" stroke="#0B5E4E" strokeWidth={2.5} fill="url(#gV)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className={`${CARD} p-6`}>
          <h3 className="font-head text-xl font-semibold mb-1">Growth & Conversions</h3>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>New members joined per month</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={growth} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE9E4" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#56626A" }} />
              <YAxis tick={{ fontSize: 11, fill: "#56626A" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EBE9E4" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="members" fill="#084438" radius={[6, 6, 0, 0]} />
              <Bar dataKey="conversions" fill="#0B5E4E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <SignupReport />
      </>)}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex gap-2 flex-wrap" data-testid="dashboard-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`dashboard-tab-${t.key}`} onClick={() => setTab(t.key)}
            className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border transition-all"
            style={tab === t.key ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {overview}
    </div>
  );
}
